import React, { useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import type { ReconciliationRowVO } from '../../../apis/types'
import ReconciliationCell from './ReconciliationCell'
import { sourceDisplayNames, fieldColumns, displayString } from './reconciliation.types'
import { useFixedTableHeader } from './useFixedTableHeader'
import { useStickyScrollbar } from '../../../components/Table/useStickyScrollbar'

interface ReconciliationTableProps {
    rows: ReconciliationRowVO[]
    totalRows: number
    utilityShortName: string | null
}

// SDGE accounts have no SA ID concept; the scraper's contract_id and OneBill's
// service_account_id were producing false discrepancies. Hide the column entirely
// for SDGE programs (backend already suppresses serviceIdMismatch).
function visibleFieldColumns(utilityShortName: string | null): typeof fieldColumns {
    if (utilityShortName === 'SDG&E') {
        return fieldColumns.filter((c) => c.fieldKey !== 'serviceId')
    }
    return fieldColumns
}

const colAlign = (fieldKey: string): string => {
    switch (fieldKey) {
        case 'serviceAddress':
            return 'text-left min-w-[260px]'
        case 'billingAddress':
            return 'text-left min-w-[260px]'
        case 'tariffRate':
            return 'text-left min-w-[220px]'
        case 'serviceId':
            return 'text-left min-w-[140px]'
        case 'lastStmtDueDate':
            return 'text-center min-w-[110px]'
        case 'lastPaymentDate':
            return 'text-center min-w-[110px]'
        default:
            return 'text-center min-w-[85px]'
    }
}

const HeaderRow: React.FC<{ columns: typeof fieldColumns }> = ({ columns }) => (
    <tr>
        <th className="px-4 py-3 text-left text-sm font-medium text-slate-500 dark:text-slate-400 sticky left-0 bg-white dark:bg-slate-900 z-10 min-w-[200px] border-r border-slate-100 dark:border-slate-700">
            Account Name
            <br /><span className="font-normal text-[10px] text-slate-400 dark:text-slate-500">from Utility Portal</span>
        </th>
        <th className="px-4 py-3 text-left text-sm font-medium text-slate-500 dark:text-slate-400 min-w-[145px]">
            Utility Account
            <br /><span className="font-normal text-[10px] text-slate-400 dark:text-slate-500">from OneBill</span>
        </th>
        <th className="px-4 py-3 text-left text-sm font-medium text-slate-500 dark:text-slate-400 min-w-[115px]">
            OneBill Account
            <br /><span className="font-normal text-[10px] text-slate-400 dark:text-slate-500">from OneBill</span>
        </th>
        <th className="px-4 py-3 text-left text-sm font-medium text-slate-500 dark:text-slate-400 min-w-[180px]">
            OneBill Name
            <br /><span className="font-normal text-[10px] text-slate-400 dark:text-slate-500">from OneBill</span>
        </th>
        <th className="px-4 py-3 text-left text-sm font-medium text-slate-500 dark:text-slate-400 min-w-[100px]">
            Meter Number
            <br /><span className="font-normal text-[10px] text-slate-400 dark:text-slate-500">from OneBill</span>
        </th>
        <th className="px-4 py-3 text-left text-sm font-medium text-slate-500 dark:text-slate-400 min-w-[220px]">
            Admin Service Address
            <br /><span className="font-normal text-[10px] text-slate-400 dark:text-slate-500">from Admin</span>
        </th>
        <th className="px-4 py-3 text-left text-sm font-medium text-slate-500 dark:text-slate-400 min-w-[220px]">
            Admin Billing Address
            <br /><span className="font-normal text-[10px] text-slate-400 dark:text-slate-500">from Admin</span>
        </th>
        <th className="px-4 py-3 text-left text-sm font-medium text-slate-500 dark:text-slate-400 min-w-[150px]">
            Billing Mode
            <br /><span className="font-normal text-[10px] text-slate-400 dark:text-slate-500">from OneBill</span>
        </th>
        {columns.map((col) => (
            <th
                key={col.fieldKey}
                className={`px-4 py-3 text-sm font-medium text-slate-500 dark:text-slate-400 ${colAlign(col.fieldKey)}`}
            >
                {col.label}
                <br /><span className="font-normal text-[10px] text-slate-400 dark:text-slate-500">from {sourceDisplayNames[col.authoritativeSource]}</span>
            </th>
        ))}
    </tr>
)

const ReconciliationTable: React.FC<ReconciliationTableProps> = ({ rows, totalRows, utilityShortName }) => {
    const columns = visibleFieldColumns(utilityShortName)
    const theadRef = useRef<HTMLTableSectionElement>(null)
    const scrollRef = useRef<HTMLDivElement>(null)
    const wrapperRef = useRef<HTMLDivElement>(null)
    const fixedScrollRef = useRef<HTMLDivElement>(null)

    const handleScrollSync = useCallback((left: number) => {
        if (fixedScrollRef.current) {
            fixedScrollRef.current.scrollLeft = left
        }
    }, [])

    const { isFixed, portalLeft, portalRight } = useFixedTableHeader(theadRef, scrollRef, wrapperRef, handleScrollSync)
    const { stickyRef, scrollWidth, hasHorizontalOverflow } = useStickyScrollbar(scrollRef, rows.length > 0)

    if (rows.length === 0) {
        return <div className="text-sm font-medium dark:text-white py-4">No accounts match the current filters.</div>
    }

    return (
        <>
        <div ref={wrapperRef} className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden recon-table">
            {isFixed && createPortal(
                <div className="fixed top-0 left-0 right-0 z-50 pointer-events-none">
                    <div
                        className="pointer-events-auto bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 shadow-md"
                        style={{
                            marginLeft: portalLeft,
                            marginRight: portalRight,
                        }}
                    >
                        <div ref={fixedScrollRef} className="overflow-hidden">
                            <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
                                <thead className="bg-white dark:bg-slate-900">
                                    <HeaderRow columns={columns} />
                                </thead>
                            </table>
                        </div>
                    </div>
                </div>,
                document.body
            )}
            <div ref={scrollRef} className="overflow-x-scroll scrollbar-hidden">
                <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
                    <thead ref={theadRef} className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
                        <HeaderRow columns={columns} />
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                        {rows.map((row, index) => {
                            const isInactive = row.issues.includes('inactiveMeter')
                            const stripeBg = index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-slate-50/50 dark:bg-slate-850/50'
                            const stickyBg = index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-slate-50 dark:bg-slate-850'
                            const accountName = displayString(row.fields.accountName)
                            const hasName = !!accountName

                            return (
                                <tr
                                    key={`${row.utilityAccountNumber}-${index}`}
                                    className={`${stripeBg} hover:bg-indigo-50/30 dark:hover:bg-indigo-900/10 transition-colors ${isInactive ? 'opacity-50' : ''}`}
                                >
                                    <td
                                        className={`px-4 py-3 text-sm font-medium sticky left-0 z-10 border-r border-slate-100 dark:border-slate-700 ${hasName || isInactive ? stickyBg : `${stickyBg} bg-red-50 dark:bg-red-900/20`}`}
                                        title={isInactive ? 'Inactive meter — this service account has been closed' : hasName ? 'From utility portal' : 'Account name missing — may need to be scraped or entered manually.'}
                                    >
                                        <span className="flex items-center gap-2">
                                            {hasName ? (
                                                <span className="dark:text-slate-300">
                                                    {accountName}
                                                    {row.fields.accountName?.status === 'mismatch' && row.fields.accountName?.mismatch && (
                                                        <span className="block text-[10px] text-orange-500 font-medium mt-0.5">
                                                            &ne; {row.fields.accountName.mismatch.comparisonValue}
                                                            <span className="text-slate-400 ml-1">
                                                                in {(() => {
                                                                    const field = row.fields.accountName
                                                                    const otherKey = Object.keys(field.values).find(s => s !== field.authoritativeSource)
                                                                    return otherKey ? (sourceDisplayNames[otherKey as keyof typeof sourceDisplayNames] ?? otherKey) : 'the other source'
                                                                })()}
                                                            </span>
                                                        </span>
                                                    )}
                                                </span>
                                            ) : !isInactive
                                                ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset bg-red-100 dark:bg-red-400/10 text-red-700 dark:text-red-400 ring-red-600/10 dark:ring-red-400/20">MISSING</span>
                                                : <span className="text-slate-400 dark:text-slate-500">&mdash;</span>
                                            }
                                            {row.tenantType === 'National' && (
                                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${row.isTopNationalTarget ? 'bg-indigo-100 dark:bg-indigo-400/10 text-indigo-700 dark:text-indigo-400 ring-1 ring-inset ring-indigo-600/10 dark:ring-indigo-400/20' : 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400'}`}>
                                                    {row.isTopNationalTarget ? '★ NAT' : 'NAT'}
                                                </span>
                                            )}
                                            {isInactive && (
                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400">INACTIVE</span>
                                            )}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-xs font-mono text-slate-700 dark:text-slate-300 whitespace-nowrap" title="From OneBill">
                                        {row.utilityAccountNumber}
                                    </td>
                                    <td className="px-4 py-3 text-xs font-mono whitespace-nowrap" title="Open Admin tenant page for this account">
                                        <Link
                                            to={`/tenant/tenants/${row.tenantCuid}?serviceAccountNumber=${encodeURIComponent(row.onebillAccountNumber)}`}
                                            className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-200 hover:underline"
                                        >
                                            {row.onebillAccountNumber}
                                        </Link>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300" title="OneBill account name">
                                        {row.onebillAccountName ?? <span className="text-slate-300 dark:text-slate-600">&mdash;</span>}
                                    </td>
                                    <td className="px-4 py-3 text-xs font-mono text-slate-700 dark:text-slate-300 whitespace-nowrap" title="From OneBill">
                                        {row.meterNumber ?? <span className="text-slate-300 dark:text-slate-600">&mdash;</span>}
                                    </td>
                                    <td className="px-4 py-3 text-xs text-slate-700 dark:text-slate-300 min-w-[220px]" title="Service address from Admin">
                                        {row.adminServiceAddress ?? <span className="text-slate-300 dark:text-slate-600">&mdash;</span>}
                                    </td>
                                    <td className="px-4 py-3 text-xs text-slate-700 dark:text-slate-300 min-w-[220px]" title="Billing address from Admin">
                                        {row.adminBillingAddress ?? <span className="text-slate-300 dark:text-slate-600">&mdash;</span>}
                                    </td>
                                    <td className="px-4 py-3 text-xs font-mono text-slate-700 dark:text-slate-300" title="Billing mode from OneBill — drives whether 'Not linked' is an issue (CONSOLIDATED/STANDALONE require a linked scraper).">
                                        {row.billingMode ?? <span className="text-slate-300 dark:text-slate-600">&mdash;</span>}
                                    </td>
                                    {columns.map((col) => (
                                        <ReconciliationCell
                                            key={col.fieldKey}
                                            field={row.fields[col.fieldKey]}
                                            fieldKey={col.fieldKey}
                                            lastPaymentAmount={col.fieldKey === 'lastPaymentDate' ? row.fields.lastPaymentAmount : undefined}
                                            isInactive={isInactive}
                                            scraperAccountExists={col.fieldKey === 'scraperLinked' ? row.scraperAccountExists : undefined}
                                            scraperLinkedIsIssue={col.fieldKey === 'scraperLinked' ? row.issues.includes('scraperNotLinked') : undefined}
                                        />
                                    ))}
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
            <div className="px-4 py-3 flex items-center justify-between text-sm border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                <div className="text-slate-500 dark:text-slate-400">
                    Showing <span className="font-medium text-slate-700 dark:text-slate-300">{rows.length}</span> of <span className="font-medium text-slate-700 dark:text-slate-300">{totalRows}</span> accounts
                </div>
                <div className="text-xs text-slate-400 dark:text-slate-500">Sorted by utility account number</div>
            </div>
        </div>
        {hasHorizontalOverflow ? (
            <div
                ref={stickyRef}
                className="sticky bottom-0 z-20 overflow-x-auto scrollbar-thin"
            >
                <div style={{ width: scrollWidth, height: 1 }} />
            </div>
        ) : null}
        </>
    )
}

export default ReconciliationTable
